import React, { useCallback, useState } from "react";

import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import useGetPortfolioPlans from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlans";
import { ClientId, PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import PortfolioPlanList from "../PortfolioPlanList";
import DrawerHeader from "@src/multiCustodian/components/Advisor/Investments/ModelMarketplaceV2/PortfolioPlanTab/ModelDrawer/BaseDrawer/DrawerHeader";
import Drawer, {
  largeDrawer,
} from "@src/multiCustodian/components/Drawer/Drawer";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import CustomDropdownBase from "@src/sharedComponents/CustomDropdownBase/CustomDropdownBase";
import DropdownMenuOptions from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";

import ModelTab, { TabKey } from "./Tab";

const infinitePagination = {
  page: "1",
  pageSize: "100",
  orderBy: "createdAt desc",
} as const;

const BaseDrawer: React.FC<React.PropsWithChildren<object>> = ({
  children,
}) => {
  const navigate = useNavigate();

  const { portfolioPlanId, clientId } = useParams<{
    portfolioPlanId: PortfolioPlanId;
    clientId: ClientId;
  }>();

  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const location = useLocation();

  const locationPath = location.pathname.split("/");
  // if we are here, it means that the last element of the path should be either "Accounts" | "MoreDetails"
  const locationTab = locationPath[locationPath.length - 1] as TabKey;

  const auth = useAdvisorRequestAuth();

  const { data: portfolioPlan, isLoading: isLoadingPortfolioPlan } =
    useGetPortfolioPlanById(portfolioPlanId, {}, auth);

  const portfolioPlansData = useGetPortfolioPlans(infinitePagination, auth);

  const options =
    portfolioPlansData.data?.data
      .filter((plan) => plan.plan.portfolioPlanId !== portfolioPlanId)
      .map((plan) => ({
        label: plan.plan.name,
        id: plan.plan.portfolioPlanId,
      })) ?? [];

  const findSelectedDrop = useCallback(
    ({ id }: { id: string; label: string }) => {
      navigate({
        ...location,
        pathname: `/Client/${clientId}/Investments/PortfoliosV2/${id}/${locationTab}`,
      });

      setShowDropdown(false);
    },
    [navigate, location, clientId, locationTab]
  );

  return (
    <PortfolioPlanList>
      <Drawer
        isDrawerOpen={true}
        container_style={largeDrawer}
        transitionDuration={0}
        onClose={() =>
          navigate({
            ...location,
            pathname: `/Client/${clientId}/Investments/PortfoliosV2`,
          })
        }
        header={
          <CustomDropdownBase
            curItem={
              portfolioPlan?.data.plan.name
                ? { value: portfolioPlan?.data.plan.name }
                : undefined
            }
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            isLoading={false}
            isDisabled={false}
            width={250}
          >
            <DropdownMenuOptions
              curItemName={portfolioPlan?.data.plan.name}
              onSelect={findSelectedDrop}
              options={options}
            />
          </CustomDropdownBase>
        }
      >
        <DrawerHeader
          displayName={portfolioPlan?.data.plan.name ?? "Unknown"}
          provider={portfolioPlan?.data.plan.organization ?? "N/A"}
          isLoading={isLoadingPortfolioPlan}
        />
        <ModelTab
          clientId={clientId}
          curTab={locationTab}
          portfolioPlanId={portfolioPlanId}
        />
        {children}
      </Drawer>
    </PortfolioPlanList>
  );
};

export default BaseDrawer;
